import type { MenuProps } from 'antd';
import {
  TeamOutlined,
  TagsOutlined,
  FileProtectOutlined,
  LinkOutlined,
  UsergroupAddOutlined,
  AppstoreOutlined,
  DollarOutlined,
  BankOutlined,
  ExceptionOutlined,
  FieldTimeOutlined,
  PercentageOutlined,
  DatabaseOutlined,
  WalletOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Permission } from '@aid/shared';

type MenuItem = NonNullable<MenuProps['items']>[number];

export function buildAdminMenuItems(t: (key: string) => string): MenuItem[] {
  return [
    {
      key: '/users',
      icon: <TeamOutlined />,
      label: t('nav.users'),
    },
    {
      key: '/roles',
      icon: <TagsOutlined />,
      label: t('nav.roles'),
    },
    {
      key: '/solicitudes',
      icon: <FileProtectOutlined />,
      label: t('nav.solicitudes'),
    },
    {
      key: '/keycloak-orphans',
      icon: <LinkOutlined />,
      label: t('nav.keycloakOrphans'),
    },
  ];
}

function collectMenuKeys(items: MenuItem[]): string[] {
  return items.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];

    const key = 'key' in item && typeof item.key === 'string' ? [item.key] : [];
    const children =
      'children' in item && Array.isArray(item.children) ? collectMenuKeys(item.children) : [];

    return [...key, ...children];
  });
}

export function getSelectedMenuKey(
  pathname: string,
  items: MenuProps['items'],
): string | undefined {
  return collectMenuKeys(items ?? [])
    .filter((key) => pathname === key || pathname.startsWith(`${key}/`))
    .sort((left, right) => right.length - left.length)[0];
}

interface DominioMenuOptions {
  /**
   * Predicado por permiso. Oculta cada ítem de dominio que el usuario no puede
   * abrir, en lugar de mostrar el bloque completo por "cualquier permiso de
   * dominio".
   */
  can: (p: Permission) => boolean;
}

export function buildDominioMenuItems(
  t: (key: string) => string,
  opts: DominioMenuOptions,
): MenuItem[] {
  const { can } = opts;
  const items: MenuItem[] = [];

  if (can(Permission.COBRADOR_READ)) {
    items.push({
      key: '/mi-cobro',
      icon: <WalletOutlined />,
      label: t('nav.miCobro'),
    });
  }

  if (can(Permission.CLIENTES_READ)) {
    items.push({
      key: '/clientes',
      icon: <UsergroupAddOutlined />,
      label: t('nav.clientes'),
    });
  }
  if (can(Permission.SERVICIOS_READ)) {
    items.push({
      key: '/servicios',
      icon: <AppstoreOutlined />,
      label: t('nav.servicios'),
    });
  }
  if (can(Permission.COBROS_READ)) {
    const facturasChildren: MenuItem[] = [
      {
        key: '/facturas',
        label: t('nav.facturasLista'),
      },
    ];
    if (can(Permission.COBROS_GENERAR)) {
      facturasChildren.push({
        key: '/facturas/generar',
        label: t('nav.generarFacturas'),
      });
    }
    facturasChildren.push({
      key: '/facturas/historial-generacion',
      label: t('nav.historialGeneracion'),
    });
    items.push({
      key: 'facturas-group',
      icon: <DollarOutlined />,
      label: t('nav.cobros'),
      children: facturasChildren,
    });
    items.push({
      key: '/reportes/corte',
      icon: <WarningOutlined />,
      label: t('nav.corteMora'),
    });
  }
  if (can(Permission.PAGOS_READ)) {
    items.push({
      key: '/pagos',
      icon: <BankOutlined />,
      label: t('nav.pagos'),
    });
  }
  if (can(Permission.EXCEPCIONES_READ)) {
    items.push({
      key: '/excepciones',
      icon: <ExceptionOutlined />,
      label: t('nav.excepciones'),
    });
  }
  if (can(Permission.VISITAS_READ)) {
    items.push({
      key: '/visitas',
      icon: <FieldTimeOutlined />,
      label: t('nav.visitas'),
    });
  }
  if (can(Permission.COMISIONES_READ)) {
    items.push({
      key: '/comisiones',
      icon: <PercentageOutlined />,
      label: t('nav.comisiones'),
    });
  }

  const catalogoChildren: MenuItem[] = [];
  if (can(Permission.CATALOGOS_READ)) {
    catalogoChildren.push({ key: '/catalogos', label: t('nav.catalogos') });
  }
  if (can(Permission.PLANES_READ)) {
    catalogoChildren.push({ key: '/planes', label: t('nav.planes') });
  }
  if (can(Permission.TIPOS_SERVICIO_READ)) {
    catalogoChildren.push({
      key: '/tipos-servicio',
      label: t('nav.tiposServicio'),
    });
  }
  if (catalogoChildren.length > 0) {
    items.push({
      key: 'catalogos-group',
      icon: <DatabaseOutlined />,
      label: t('nav.catalogos'),
      children: catalogoChildren,
    });
  }

  return items;
}
